import client, {QueryOptions} from "@irati/utils/services/apollo"
import {store} from "@irati/store"
import WalletActions from "@irati/wallet/store/actions/Wallet"
import {
  Deal,
  Payment,
  PaymentOrder,
  RewardOrder,
  Wallet,
  AdyenProcessedPayment,
  AdyenAdditionalDetails,
  PaymentMethod,
  ProviderSettings,
  PaymentMethodsInput,
  Platform,
  WellnessMedia,
  QueryCompletePaymentArgs,
  MultiCurrencyDetailsInput
} from "@irati/types"

import makePaymentCardPayload, {
  makePaymentRewardWalletPayload,
  makeWellnessPaymentPayload,
  makePaymentWalletPayload,
  makeZipPayload
} from "../utils/makePaymentPayload"

import {PaymentProvider} from "../enums"
import getPaymentOrderQuery from "./queries/getPaymentOrder"
import getPaymentMethodsQuery, {paymentMethodsKey} from "./queries/getPaymentMethods"
import getProviderSettingsQuery, {providerSettingsKey} from "./queries/getProviderSettings"
import payByCardMutation from "./mutations/payByCardMutation"
import payByWalletMutation from "./mutations/payByWalletMutation"
import payByZipMutation from "./mutations/payByZipMutation"
import adyenProcessPaymentMutation from "./mutations/adyenProcessPaymentMutation"
import adyenAdditionalDetailsMutation from "./mutations/adyenAdditionalDetailsMutation"
import completePaymentQuery, {completePaymentQueryKey} from "./queries/completePayment"
import isCheckoutDotcomEnabledQuery, {isCheckoutDotcomEnabledQueryKey} from "./queries/isCheckoutDotComEnabled"

export interface PaymentCardRequest {
  amount: number
  enableSaveCardOption: boolean
  resUrl?: string
}

export interface PaymentWalletRequest {
  amount: number
}

export interface PaymentZipRequest {
  amount: number
  resUrl: string
}

class Payments {
  getPaymentOrder(id: string, {forceRefetch = false} = {}): Promise<PaymentOrder> {
    const fetchPolicy = forceRefetch ? "network-only" : "cache-first"
    const variables = {id}

    return client.query({query: getPaymentOrderQuery, variables, fetchPolicy}).then(({data}) => data.paymentOrder)
  }
  payByAdyenDropIn(paymentOrder: PaymentOrder, details: PaymentCardRequest): Promise<Payment> {
    const variables = {
      details: makePaymentCardPayload(PaymentProvider.Adyen, paymentOrder, details),
      paymentOrderId: paymentOrder.uuid
    }

    return client.mutate({mutation: payByCardMutation, variables}).then(({data}) => data.paymentPayByCard)
  }

  adyenProcessPayment(paymentOrderId: string, paymentId: string, data: any): Promise<AdyenProcessedPayment> {
    const variables = {
      paymentOrderId,
      paymentId,
      data: JSON.stringify(data)
    }

    return client.mutate({mutation: adyenProcessPaymentMutation, variables}).then((res) => {
      const action = JSON.parse(res.data.adyenProcessPayment.action)
      return {
        ...res.data.adyenProcessPayment,
        action
      }
    })
  }

  adyenAdditionalDetails(paymentOrderId: string, paymentId: string, data: any): Promise<AdyenAdditionalDetails> {
    const variables = {
      paymentOrderId,
      paymentId,
      data: JSON.stringify(data)
    }

    return client.mutate({mutation: adyenAdditionalDetailsMutation, variables}).then((res) => res.data.adyenAdditionalDetails)
  }

  payByWellnessWallet(paymentOrder: PaymentOrder, wallet: Wallet, {amount}: PaymentWalletRequest, media: WellnessMedia): Promise<Payment> {
    const variables = {
      details: makeWellnessPaymentPayload(paymentOrder, wallet, {amount}, media),
      paymentOrderId: paymentOrder.uuid
    }

    return client
      .mutate({mutation: payByWalletMutation, variables})
      .then(({data}) => data.paymentPayByWallet)
      .then((payment) => {
        store.dispatch(WalletActions.get({fetchPolicy: "network-only"}, wallet.currency))
        return payment
      })
  }

  payByUserWallet(
    paymentOrder: PaymentOrder,
    wallet: Wallet,
    {amount}: PaymentWalletRequest,
    deal: Deal,
    redemptionId: string,
    multiCurrencyDetails?: MultiCurrencyDetailsInput
  ): Promise<Payment> {
    const variables = {
      details: makePaymentWalletPayload(paymentOrder, wallet, {amount}, deal, redemptionId, multiCurrencyDetails),
      paymentOrderId: paymentOrder.uuid
    }

    return client.mutate({mutation: payByWalletMutation, variables}).then(({data}) => data.paymentPayByWallet)
  }

  payByRewardWallet(paymentOrder: PaymentOrder, {amount}: PaymentWalletRequest, rewardOrder: RewardOrder): Promise<Payment> {
    return makePaymentRewardWalletPayload(paymentOrder, {amount}, rewardOrder).then((details) => {
      const variables = {
        details,
        paymentOrderId: paymentOrder.uuid
      }

      return client
        .mutate({mutation: payByWalletMutation, variables})
        .then(({data}) => data.paymentPayByWallet)
        .then((payment) => {
          store.dispatch(WalletActions.get())
          return payment
        })
    })
  }

  payByZip(paymentOrder: PaymentOrder, details: PaymentZipRequest): Promise<Payment> {
    const variables = {
      details: makeZipPayload(paymentOrder, details),
      paymentOrderId: paymentOrder.uuid
    }

    return client.mutate({mutation: payByZipMutation, variables}).then(({data}) => data.paymentPayByZip)
  }
  getPaymentMethods(variables: PaymentMethodsInput, options?: QueryOptions): Promise<PaymentMethod[]> {
    return client
      .query({...options, query: getPaymentMethodsQuery, variables})
      .then(({data: responseData}) => responseData[paymentMethodsKey])
  }
  getProviderSettings(platform: Platform, options?: QueryOptions): Promise<ProviderSettings> {
    const variables = {
      platform
    }
    return client
      .query({...options, query: getProviderSettingsQuery, variables})
      .then(({data: responseData}) => responseData[providerSettingsKey])
  }

  async completePayment(variables: QueryCompletePaymentArgs, options?: QueryOptions): Promise<Payment> {
    const {data: responseData} = await client.query({...options, query: completePaymentQuery, variables})
    return responseData[completePaymentQueryKey]
  }

  async isCheckoutDotComEnabled(options?: QueryOptions): Promise<Payment> {
    const {data: responseData} = await client.query({...options, query: isCheckoutDotcomEnabledQuery})
    return responseData[isCheckoutDotcomEnabledQueryKey]
  }
}

const paymentService = new Payments()

export const getPaymentOrder = paymentService.getPaymentOrder
export const payByAdyenDropIn = paymentService.payByAdyenDropIn
export const payByUserWallet = paymentService.payByUserWallet
export const payByRewardWallet = paymentService.payByRewardWallet
export const payByZip = paymentService.payByZip
export const adyenProcessPayment = paymentService.adyenProcessPayment
export const adyenAdditionalDetails = paymentService.adyenAdditionalDetails
export const getPaymentMethods = paymentService.getPaymentMethods
export const completePayment = paymentService.completePayment
export const isCheckoutDotComEnabled = paymentService.isCheckoutDotComEnabled

export default paymentService
